const study = {
  meta: {
    slug: "bad-credit-ecommerce-220k-holiday-inventory-620-fico",
    title:
      "$220K in 48 hours: How a 620-FICO e-commerce brand financed holiday inventory when every bank passed",
    excerpt:
      "A four-year e-commerce apparel brand doing $2.4M annually needed to pre-buy Q4 inventory to hit a Black Friday floor price. Every bank was reading the 620 personal FICO and stopping. Here's the illustrative revenue-financing structure that got them to the wire.",
    industry: "E-commerce · Direct-to-consumer apparel",
    product: "Revenue Financing (card-processing linked)",
    amount: "$220K",
    term: "9 months",
    timeToFund: "48 hours",
    state: "California",
    challenge: "Bad credit + high processing volume",
    image:
      "https://images.pexels.com/photos/12471781/pexels-photo-12471781.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-06-09",
    readTime: "5 min read",
    tags: ["bad-credit"],
    relatedArticles: [
      "bad-credit-business-loans-2026",
      "how-mca-factor-rates-work",
      "true-cost-of-merchant-cash-advance",
    ],
  },
  situation: `A four-year DTC apparel brand doing **$2.4M in trailing 12-month gross revenue** through Shopify + Amazon. Healthy business — 62% gross margin, tight ad-spend discipline, sub-3% return rate, and a full-time team of four. Personal FICO on the owner-founder: **620**, dragged down by a startup-era stretch when personal cards funded the initial product line.

The Q4 play: an overseas supplier offered a **12% pre-buy discount** on the winter line if a $185K PO could be paid in full by September 15. The math was clear — $22K in supplier savings, plus roughly $70K in additional Q4 revenue from stocking depth. But the timeline was tight.

The owner tried three institutional channels first: their existing business bank (they'd been depositing there for four years) passed at the credit check. A second bank's business unit passed on FICO. A third bank offered a $75K amount at 14% but wanted a full personal guarantee including the owner's personal residence — a non-starter.

They came to us with **17 days** until the PO deadline.`,
  structure: `**Why revenue financing, not an MCA or a term loan.** For a high-processing-volume e-commerce brand with weak personal credit, the strongest asset is the daily card revenue itself. Revenue financing (also called revenue-based financing or RBF) underwrites against processor deposits — a very different lens than a bank credit committee.

Two things worked in the borrower's favor:

1. **Card processing volume**: ~$185K/mo in credit-card sales through Shopify and Amazon
2. **Deposit consistency**: 47 consecutive months of positive-cash-flow operations

The illustrative structure that closed in 48 hours:

- **$220K advance** (covering $185K PO + $35K in Q4 marketing spend + shipping buffer)
- **Factor rate 1.30** → total payback $286,000
- **Estimated 9-month payback**, ~9% of daily card processing volume
- **No personal guarantee on the owner's residence** (guarantee limited to a corporate guarantee + owner personal-liability, not asset-secured)
- **Effective APR (avg-outstanding-balance method): ~87%** — expensive but coherent given the returned upside was $92K in supplier savings + incremental Q4 revenue
- **Prepayment discount**: 7% off remaining balance if refinanced with any documentable product before month 6

**The refi plan built in from day one:**

- **Month 1–5**: run the Q4 season, capture the supplier savings + inventory-depth revenue upside
- **Month 6**: apply for a working-capital LOC once card-processing history looks stronger + Q4 revenue reflects in the trailing-12 books
- **Month 6–9**: prepay the revenue advance using the LOC, capturing the 7% discount

**What a savvy broker would NOT do:** try to convince the borrower to skip the $185K supplier discount and "wait for cheaper money next year." That's the wrong math — cheaper capital next year does not recover the supplier discount that expires this year. The right move is to pay the expensive premium for capital that produces its own return.`,
  outcome: `Application signed on day 1 (morning). Card-processor verification and bank-statement pull completed by afternoon. **Funded at 11:20 AM on day 2** — 15 days before the supplier PO deadline.

By end of Q4:

- **$185K PO paid on time**, supplier 12% discount captured ($22K back)
- **Q4 revenue up 41% YoY** — inventory depth meant zero best-seller stockouts across the peak weeks
- **Card processing volume grew 22%** during the quarter — the revenue lender was actually getting paid back faster than the 9-month projection
- **Month 6**: working-capital LOC approved at 15.4% APR (down from 87% effective), used to prepay 62% of the remaining revenue-advance balance
- **7% prepayment discount** captured on the refinanced portion (~$8,900 net savings)
- **Personal FICO recovered to 673** by month 8, driven by the reduced personal-card utilization the LOC enabled

The takeaway: **a 620 FICO with $2.4M in clean e-commerce revenue is not the same file as a 620 FICO with $180K in revenue.** Revenue-financing products know this — they price against the business's actual cash generation, not the founder's Y2 credit-card usage. Expensive short-term capital is the right choice when it unlocks a return that could not otherwise be captured, and when there is a clear, funded refi path within 6–9 months.`,
};

export default study;
